<template>
  <div class="ai-training-page">
    <van-nav-bar fixed placeholder>
      <div slot="title" class="nav-title">{{ pageTitle }}</div>
    </van-nav-bar>

    <div class="search-wrap">
      <el-input
        v-model="keyword"
        placeholder="搜索目录或资源名称"
        size="small"
        clearable
        prefix-icon="el-icon-search"
      />
    </div>

    <div v-loading="listLoading" class="file-list">
      <div v-for="folder in displayFolders" :key="folder.id" class="folder-card" :class="{'folder-open': isExpanded(folder.id)}">
        <!-- 文件夹头部 -->
        <div class="folder-row" @click="toggleFolder(folder.id)">
          <div class="folder-icon-box">
            <i class="el-icon-folder-opened folder-icon"></i>
          </div>
          <div class="folder-text">
            <div class="folder-name">{{ folder.resourceName }}</div>
            <div class="folder-count"><span style="color:#df0012;font-weight: 400">{{ folderFiles(folder).length }} </span>个文件</div>
          </div>
          <i
            class="arrow-icon"
            :class="isExpanded(folder.id) ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"
          />
        </div>

        <!-- 文件夹展开子内容 -->
        <div v-show="isExpanded(folder.id)" class="folder-child-wrap">
          <div
            v-for="item in folderFiles(folder)"
            :key="item.id"
            class="file-item"
            @click="toPreview(item)"
          >
            <i class="el-icon-document file-icon"></i>
            <span class="file-title">{{ item.resourceName }}</span>
          </div>
          <div v-if="!folderFiles(folder).length" class="empty-tip">该目录暂无文件</div>
        </div>
      </div>

      <div v-if="!listLoading && !displayFolders.length" class="empty">暂无培训目录</div>
    </div>

    <!-- 底部提示文案（截图底部文字） -->
    <div class="page-footer">
      <p>如需查阅更多配套学习资料</p>
      <p>可使用电脑端浏览器访问官网链接 <a href="https://www.hxpxxy.cn/#/" target="_blank">https://www.hxpxxy.cn/#/</a> 进行查看</p>
    </div>
  </div>
</template>

<script>
import { getAction } from "@/api/common.js";

const PAGE_TITLE = "人工智能赋能职业教育教学关键要素改革的实践创新专题培训";
const VIDEO_FORMATS = ["mp4", "avi", "mov", "wmv", "flv", "mkv", "webm", "m4v", "mpg", "mpeg", "3gp", "rmvb", "rm", "ts"];

export default {
  name: "AiTrainingList",
  data() {
    return {
      pageTitle: PAGE_TITLE,
      listLoading: false,
      folders: [],
      expandedMap: {},
      keyword: ""
    };
  },
  computed: {
    displayFolders() {
      const kw = (this.keyword || "").trim().toLowerCase();
      if (!kw) return this.folders;
      return this.folders.filter(folder => {
        const name = (folder.resourceName || "").toLowerCase();
        if (name.indexOf(kw) !== -1) return true;
        return (folder.children || []).some(
          f => (f.resourceName || "").toLowerCase().indexOf(kw) !== -1
        );
      });
    }
  },
  watch: {
    keyword(val) {
      const kw = (val || "").trim().toLowerCase();
      if (!kw) return;
      const map = { ...this.expandedMap };
      this.displayFolders.forEach(folder => {
        const name = (folder.resourceName || "").toLowerCase();
        const hitFile = (folder.children || []).some(
          f => (f.resourceName || "").toLowerCase().indexOf(kw) !== -1
        );
        if (name.indexOf(kw) !== -1 || hitFile) {
          map[folder.id] = true;
        }
      });
      this.expandedMap = map;
    }
  },
  mounted() {
    document.title = PAGE_TITLE;
    this.loadTree();
  },
  methods: {
    folderFiles(folder) {
      const files = (folder && folder.children) || [];
      const kw = (this.keyword || "").trim().toLowerCase();
      if (!kw) return files;
      const folderHit = (folder.resourceName || "").toLowerCase().indexOf(kw) !== -1;
      if (folderHit) return files;
      return files.filter(
        f => (f.resourceName || "").toLowerCase().indexOf(kw) !== -1
      );
    },
    isExpanded(id) {
      return !!this.expandedMap[id];
    },
    toggleFolder(id) {
      this.$set(this.expandedMap, id, !this.expandedMap[id]);
    },
    loadTree() {
      this.listLoading = true;
      getAction("/api-study/aiTraining/public/resource/tree")
        .then(res => {
          if (res.resp_code === 0) {
            this.folders = this.filterVideoFolders(res.datas || []);
            this.initExpanded();
          } else {
            this.folders = [];
            this.$message.error((res && res.resp_msg) || "获取资源失败");
          }
        })
        .catch(() => {
          this.$message.error("获取资源失败");
        })
        .finally(() => {
          this.listLoading = false;
        });
    },
    filterVideoFolders(folders) {
      return folders
        .map(folder => ({
          ...folder,
          children: (folder.children || []).filter(
            child => !VIDEO_FORMATS.includes((child.fileFormat || "").toLowerCase())
          )
        }))
        .filter(folder => folder.children && folder.children.length > 0);
    },
    initExpanded() {
      const map = {};
      const queryId = this.$route.query.id;
      if (queryId) {
        for (let i = 0; i < this.folders.length; i++) {
          const folder = this.folders[i];
          const hit = (folder.children || []).find(
            f => String(f.id) === String(queryId)
          );
          if (hit) {
            map[folder.id] = true;
            this.expandedMap = map;
            return;
          }
        }
      }
      if (this.folders.length) {
        map[this.folders[0].id] = true;
      }
      this.expandedMap = map;
    },
    toPreview(item) {
      this.$router.push({
        name: "aiTrainingPreview",
        query: { id: item.id }
      });
    },
    formatLabel(format) {
      const f = (format || "").toLowerCase();
      if (!f) return "FILE";
      if (f === "jpeg") return "JPG";
      return f.toUpperCase().slice(0, 4);
    },
    formatSize(size) {
      if (size == null || size === "") return "-";
      const n = Number(size);
      if (isNaN(n)) return "-";
      if (n < 1024) return n + "B";
      if (n < 1024 * 1024) return (n / 1024).toFixed(1) + "KB";
      return (n / (1024 * 1024)).toFixed(1) + "MB";
    },
    formatTime(val) {
      if (!val) return "";
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val).slice(0, 16);
      const pad = n => (n < 10 ? "0" + n : "" + n);
      return (
        d.getFullYear() +
        "-" +
        pad(d.getMonth() + 1) +
        "-" +
        pad(d.getDate()) +
        " " +
        pad(d.getHours()) +
        ":" +
        pad(d.getMinutes())
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-training-page {
  min-height: 100%;
  background: #ffffff;
  padding-bottom: 30px;
}
.nav-title {
  font-size: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// 搜索框区域
.search-wrap {
  padding: 12px 16px;
  .el-input__inner {
    background-color: #f5f5f5;
    border-radius: 8px;
    border: none;
  }
}

.file-list {
  padding: 0 12px;
}

// 文件夹卡片容器
.folder-card {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #eee;
  margin-bottom: 10px;
  overflow: hidden;
  &.folder-open {
    border: 1px dashed #dd4444;
    // 展开后 folder-name 样式
    .folder-name {
      color: #6a0e16;
      font-weight: bold; // 或 700
    }
  }
}

// 文件夹头部行
.folder-row {
  display: flex;
  align-items: center;
  padding: 14px 12px;
  cursor: pointer;
}
.folder-icon-box {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background-color: #dd2828;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  .folder-icon {
    color: #fff;
    font-size: 22px;
  }
}
.folder-text {
  flex: 1;
  margin-left: 10px;
  min-width: 0;
}
.folder-name {
  font-size: 18px;
  color: #222;
  font-weight: bold;
}
.folder-count {
  font-size: 13px;
  color: #999;
  margin-top: 2px;
}
.arrow-icon {
  font-size: 16px;
  color: #bbbbbb;
}

// 展开子文件区域
.folder-child-wrap {
  padding: 4px 0 8px;
  background-color: #fff;
}
.file-item {
  display: flex;
  align-items: center;
  padding: 10px 0px 10px 15px;
  margin: 0px 10px;
  cursor: pointer;
  border-bottom: 1px solid #e1e1e1;   // 新增下划线
  &:last-child {
    border-bottom: none;               // 最后一个去掉下划线
  }
  .file-icon {
    color: #666;
    font-size: 16px;
    margin-right: 8px;
  }
  .file-title {
    font-size: 16px;
    color: #333;
    font-weight: 600;                  // 适当加粗（可用 bold）
  }
}
.empty-tip {
  padding: 10px 16px 10px 52px;
  font-size:13px;
  color:#999;
}

// 底部文字
.page-footer {
  margin-top: 30px;
  text-align: center;
  padding: 0 20px;
  color: #888888;
  font-size: 13px;
  line-height: 1.6;
  p {
    margin: 2px 0;
  }
  a {
    color: #3678d8;
    word-break: break-all;
  }
}

.empty {
  text-align: center;
  color: #909399;
  padding: 60px 0;
  font-size: 14px;
}
</style>



// WEBPACK FOOTER //
// src/views/aiTraining/index.vue